<template>
  <div class="container mt-5 signup-container">
    <h2 class="text-center">Sign Up</h2>
    <form @submit.prevent="$emit('submit-signup')" class="mt-4">
      <div class="mb-3">
        <label for="firstName" class="form-label">First Name</label>
        <input type="text" class="form-control" id="firstName" v-model="form.firstName" required />
      </div>
      <div class="mb-3">
        <label for="lastName" class="form-label">Last Name</label>
        <input type="text" class="form-control" id="lastName" v-model="form.lastName" required />
      </div>
      <div class="mb-3">
        <label for="email" class="form-label">Email</label>
        <input 
          type="email" 
          class="form-control" 
          id="email" 
          v-model="form.email" 
          @blur="validateEmail"
          @input="clearEmailError"
          required 
        />
        <div v-if="emailError" class="text-danger" v-html="emailError">
        </div>
      </div>

      <div class="mb-3">
        <label for="dateOfBirth" class="form-label">Date of Birth (MM/DD/YYYY)</label>
        <input 
          type="text" 
          class="form-control" 
          id="dateOfBirth" 
          v-model="form.dateOfBirth"
          placeholder="MM/DD/YYYY"
          autocomplete="bday"
          maxlength="10"
          @input="formatDateInput"
          @blur="validateDateOfBirth"
          required
        />
        <div v-if="dateOfBirthError" class="text-danger">
          {{ dateOfBirthError }}
        </div>
      </div>
      <div class="mb-3">
        <label for="password" class="form-label">Password</label>
        <input 
          type="password" 
          class="form-control" 
          id="password" 
          v-model="form.password" 
          @input="checkPasswordsMatch" 
          required
        />
      </div>
      <div class="mb-3">
        <label for="verifyPassword" class="form-label">Verify Password</label>
        <input 
          type="password" 
          class="form-control" 
          id="verifyPassword" 
          v-model="form.verifyPassword" 
          @input="checkPasswordsMatch" 
          required
        />
      </div>
      <div v-if="!passwordsMatch" class="text-danger mb-3">
        Passwords do not match
      </div>

      <!-- Terms and Conditions Checkbox -->
      <div class="mb-3 form-check">
        <input type="checkbox" class="form-check-input" id="terms" v-model="form.agreeToTerms" required />
        <label class="form-check-label" for="terms">
          I agree to the
          <a 
            href="#" 
            class="text-primary" 
            @click.prevent="toggleTermsModal"
          >
            Terms and Conditions
          </a>
        </label>

        <!-- Render the TermsAndConditionsView if needed -->
        <TermsAndConditionsView
          v-if="showTermsAndConditions"
          @close="showTermsAndConditions = false"
        />
      </div>

      <!-- Submit Button -->
      <button 
        type="button" 
        class="btn btn-primary w-100" 
        @click="submitToApi"
        :disabled="!passwordsMatch || !form.password || !form.verifyPassword || !form.agreeToTerms || emailError || dateOfBirthError"
      >
        Create Account
      </button>

      <button 
        type="button" 
        class="btn btn-link w-100 mt-2" 
        @click="$emit('showHome')"
      >
        Back to Home
      </button>
      <!-- Display general error message -->
      <div v-if="errorMessage" class="alert alert-danger mt-3">
        {{ errorMessage }}
      </div>
    </form>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import TermsAndConditionsView from "./TermsAndConditionsView";

export default {
  name: "SignUp",
  data() {
    return {
      form: {
        firstName: '',
        lastName: '',
        email: '',
        dateOfBirth: '',
        password: '',
        verifyPassword: '',
        agreeToTerms: false,
      },
      states: [], // Initialize as needed
      passwordsMatch: true,
      showTermsAndConditions: false,
      isLoading: false,
      errorMessage: null,
      dateOfBirthError: null,
      emailError: null,
    };
  },
  components: {
    TermsAndConditionsView,
  },
  methods: {
    ...mapActions(['login', 'setDrawPoints']), // Map Vuex actions

    toggleTermsModal() {
      this.showTermsAndConditions = !this.showTermsAndConditions;
    },

    checkPasswordsMatch() {
      this.passwordsMatch = this.form.password === this.form.verifyPassword;
    },

    validateEmail() {
      // Reset error
      this.emailError = null;
      
      // Skip validation if empty
      if (!this.form.email) {
        return;
      }
      
      // Email regex pattern
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(this.form.email)) {
        this.emailError = "Please enter a valid email address";
        return false;
      }
      
      return true;
    },
    
    clearEmailError() {
      if (this.emailError) {
        this.emailError = null;
      }
    },

    formatDateInput() {
      // Strip any non-numeric characters
      let input = this.form.dateOfBirth.replace(/\D/g, '');
      
      // Add slashes automatically
      if (input.length > 2) {
        input = input.slice(0, 2) + '/' + input.slice(2);
      }
      if (input.length > 5) {
        input = input.slice(0, 5) + '/' + input.slice(5, 9);
      }
      
      // Update the form value
      this.form.dateOfBirth = input;
      
      // Clear error when typing
      if (this.dateOfBirthError) {
        this.dateOfBirthError = null;
      }
    },
    
    validateDateOfBirth() {
      // Reset error
      this.dateOfBirthError = null;
      
      // Skip validation if empty
      if (!this.form.dateOfBirth) {
        return;
      }
      
      // Check format (MM/DD/YYYY)
      const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
      if (!dateRegex.test(this.form.dateOfBirth)) {
        this.dateOfBirthError = "Please enter a valid date in MM/DD/YYYY format";
        return;
      }
      
      // Check if date is valid
      const [month, day, year] = this.form.dateOfBirth.split('/').map(Number);
      const date = new Date(year, month - 1, day);
      
      // Verify the date is valid (if month or day are invalid, the date object will adjust accordingly)
      if (date.getMonth() !== month - 1 || date.getDate() !== day) {
        this.dateOfBirthError = "Please enter a valid date";
        return;
      }
      
      // Check if date is in the past and not too far back (e.g., over 120 years)
      const now = new Date();
      if (date > now) {
        this.dateOfBirthError = "Date of birth must be in the past";
        return;
      }
      
      // Check if user is at least 13 years old (common minimum age requirement)
      const thirteenYearsAgo = new Date();
      thirteenYearsAgo.setFullYear(now.getFullYear() - 13);
      if (date > thirteenYearsAgo) {
        this.dateOfBirthError = "You must be at least 13 years old to sign up";
        return;
      }
      
      // The validation passed, but don't modify the display format
      // We'll convert to ISO format only when submitting to the API
    },
    
    async submitToApi() {
      // Validate email and date of birth before submitting
      const isEmailValid = this.validateEmail();
      this.validateDateOfBirth();
      
      // Return if validation fails
      if (this.emailError || this.dateOfBirthError) {
        return;
      }
      
      this.isLoading = true;
      this.errorMessage = null; // reset error message
      this.emailError = null; // reset email error
      try {
        // Convert MM/DD/YYYY to YYYY-MM-DD format for API
        let apiFormattedDate = this.form.dateOfBirth;
        if (this.form.dateOfBirth && this.form.dateOfBirth.includes('/')) {
          const [month, day, year] = this.form.dateOfBirth.split('/').map(Number);
          apiFormattedDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        }
        
        const customerDTO = {
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          email: this.form.email,
          country: "USA",
          dateOfBirth: apiFormattedDate,
          password: this.form.password,
        };

        const customerSignupDTO = {
          customerDTO: customerDTO,
          stateLoginDTOS: [], // Adjust if you have state login data
          termsAndConditionsAccepted: this.form.agreeToTerms,
        };

        const response = await fetch(this.$API_BASE_URL + "/api/customers", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(customerSignupDTO),
        });

        if (response.ok) {
          const responseData = await response.json();
          const expiration = responseData.sessionToken.expiration;

          this.login(expiration);  // Vuex login
          this.$emit('show-sync-points-utah');
        } else {
          const errorData = await response.json();
          
          // Check if the error is due to email already in use
          if (errorData.code === "INVALID_DATA" && 
              errorData.message === "This email address is already in use") {
            // Set the email error message
            this.emailError = "This email address is already in use. Forgot your password? <span class=\"text-primary reset-link\">Reset your password</span>";
            // Add a slight delay to ensure DOM is updated before adding event listener
            setTimeout(() => {
              const resetLink = document.querySelector('.reset-link');
              if (resetLink) {
                resetLink.style.cursor = 'pointer';
                resetLink.addEventListener('click', () => {
                  // Store the user's email in the parent component
                  this.$parent.forgotPasswordEmail = this.form.email;
                  this.$emit('show-forgot-password');
                });
              }
            }, 0);
          } else {
            this.errorMessage = `Error: ${errorData.message || 'Unknown error occurred'}`;
          }
        }
      } catch (error) {
        this.errorMessage = `Error: ${error.message}`;
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style>
.signup-container {
  max-height: 100vh; /* Ensure the container doesn't exceed the viewport height */
  overflow-y: auto; /* Make the container scrollable if content overflows */
  padding-bottom: 20px; /* Add some padding at the bottom */
}

.reset-link {
  cursor: pointer;
  text-decoration: underline;
}
</style>
